import React, { useState } from "react";
import styled from "styled-components";
import { NavLink, withRouter } from "react-router-dom";
import { darken, rgba } from "polished";
import PerfectScrollbar from "react-perfect-scrollbar";
import "../vendor/perfect-scrollbar.css";
import { ReactComponent as Logo } from "../vendor/logo.svg";

import {
    Badge,
    Chip,
    Grid,
    Avatar,
    Collapse,
    Drawer as MuiDrawer,
    List as MuiList,
    ListItem,
    ListItemText,
    Typography,
    ListItemProps,
    DrawerProps,
    ListProps,
    ListItemTextProps,
    Box,
} from "@material-ui/core";

import { ExpandLess, ExpandMore } from "@material-ui/icons";

import AppContext from "../context/AppContext";
import Role from "./Role";

const Drawer = styled(MuiDrawer)`
    border-right: 0;

    > div {
        border-right: 0;
    }
` as React.ComponentType<DrawerProps>;

const Scrollbar = styled(PerfectScrollbar)`
    background-color: ${(props) => props.theme.sidebar.background};
    border-right: 1px solid rgba(0, 0, 0, 0.12);
`;

const List = styled(MuiList)`
    background-color: ${(props) => props.theme.sidebar.background};
` as React.ComponentType<ListProps>;

const Items = styled.div`
    padding-top: ${(props) => props.theme.spacing(2.5)}px;
    padding-bottom: ${(props) => props.theme.spacing(2.5)}px;
`;

const Brand = styled(ListItem)`
    font-size: ${(props) => props.theme.typography.h5.fontSize};
    font-weight: ${(props) => props.theme.typography.fontWeightMedium};
    color: ${(props) => props.theme.sidebar.header.color};
    background-color: ${(props) => props.theme.sidebar.header.background};
    font-family: ${(props) => props.theme.typography.fontFamily};
    min-height: 56px;
    padding-left: ${(props) => props.theme.spacing(6)}px;
    padding-right: ${(props) => props.theme.spacing(6)}px;
    justify-content: center;
    cursor: pointer;

    ${(props) => props.theme.breakpoints.up("sm")} {
        min-height: 64px;
    }

    &:hover {
        background-color: ${(props) => props.theme.sidebar.header.background};
    }

    &:link {
        text-decoration: none;
    }
` as React.ComponentType<ListItemProps>;

const Category = styled(ListItem)`
    padding-top: ${(props) => props.theme.spacing(3)}px;
    padding-bottom: ${(props) => props.theme.spacing(3)}px;
    padding-left: ${(props) => props.theme.spacing(8)}px;
    padding-right: ${(props) => props.theme.spacing(7)}px;
    font-weight: ${(props) => props.theme.typography.fontWeightRegular};

    svg {
        color: ${(props) => props.theme.sidebar.color};
        font-size: 20px;
        width: 20px;
        height: 20px;
        opacity: 0.5;
    }

    &:hover {
        background: rgba(0, 0, 0, 0.08);
    }

    &.${({ activeClassName }: { activeClassName: String }) =>
            activeClassName.toString()} {
        background-color: ${(props) =>
            darken(0.03, props.theme.sidebar.background)};

        span {
            color: ${(props) => props.theme.sidebar.color};
        }
    }
` as React.ComponentType<ListItemProps>;

const CategoryText = styled(ListItemText)`
    margin: 0;
    span {
        color: ${(props) => props.theme.sidebar.color};
        font-size: ${(props) => props.theme.typography.body1.fontSize}px;
        padding: 0 ${(props) => props.theme.spacing(4)}px;
    }
` as React.ComponentType<ListItemTextProps>;

const CategoryIconLess = styled(ExpandLess)`
    color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`;

const CategoryIconMore = styled(ExpandMore)`
    color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`;

const Link = styled(ListItem)`
    padding-left: ${(props) => props.theme.spacing(17.5)}px;
    padding-top: ${(props) => props.theme.spacing(2)}px;
    padding-bottom: ${(props) => props.theme.spacing(2)}px;

    span {
        color: ${(props) => rgba(props.theme.sidebar.color, 0.7)};
    }

    &:hover span {
        color: ${(props) => rgba(props.theme.sidebar.color, 0.9)};
    }

    &:hover {
        background-color: ${(props) =>
            darken(0.015, props.theme.sidebar.background)};
    }

    &.${({ activeClassName }: { activeClassName: String }) =>
            activeClassName.toString()} {
        background-color: ${(props) =>
            darken(0.03, props.theme.sidebar.background)};

        span {
            color: ${(props) => props.theme.sidebar.color};
        }
    }
` as React.ComponentType<ListItemProps>;

const LinkText = styled(ListItemText)`
    color: ${(props) => props.theme.sidebar.color};
    span {
        font-size: ${(props) => props.theme.typography.body1.fontSize}px;
    }
    margin-top: 0;
    margin-bottom: 0;
` as React.ComponentType<ListItemTextProps>;

const LinkBadge = styled(Chip)`
    font-size: 11px;
    font-weight: ${(props) => props.theme.typography.fontWeightBold};
    height: 20px;
    position: absolute;
    right: 28px;
    top: 8px;
    background: ${(props) => props.theme.sidebar.badge.background};

    span.MuiChip-label,
    span.MuiChip-label:hover {
        cursor: pointer;
        color: ${(props) => props.theme.sidebar.badge.color};
        padding-left: ${(props) => props.theme.spacing(2)}px;
        padding-right: ${(props) => props.theme.spacing(2)}px;
    }
`;

const CategoryBadge = styled(LinkBadge)`
    top: 12px;
`;

const SidebarSection = styled(Typography)`
    color: ${(props) => props.theme.sidebar.color};
    padding: ${(props) => props.theme.spacing(4)}px
        ${(props) => props.theme.spacing(7)}px
        ${(props) => props.theme.spacing(1)}px;
    opacity: 0.9;
    display: block;
`;

const SidebarFooter = styled.div`
    background-color: ${(props) =>
        props.theme.sidebar.footer.background} !important;
    padding: ${(props) => props.theme.spacing(2.75)}px
        ${(props) => props.theme.spacing(4)}px;
    border-right: 1px solid rgba(0, 0, 0, 0.12);
`;

const SidebarFooterText = styled(Typography)`
    color: ${(props) => props.theme.sidebar.footer.color};
`;

const SidebarFooterSubText = styled(Typography)`
    color: ${(props) => props.theme.sidebar.footer.color};
    font-size: 0.7rem;
    display: block;
    padding: 1px;
`;

const SidebarFooterBadge = styled(Badge)`
    margin-right: ${(props) => props.theme.spacing(1)}px;
    span {
        background-color: ${(props) =>
            props.theme.sidebar.footer.online.background};
        border: 1.5px solid ${(props) => props.theme.palette.common.white};
        height: 12px;
        width: 12px;
        border-radius: 50%;
    }
`;

const BrandIcon = styled(Logo)`
    margin-right: ${(props) => props.theme.spacing(2)}px;
    color: ${(props) => props.theme.sidebar.header.brand.color};
    fill: ${(props) => props.theme.sidebar.header.brand.color};
    width: 32px;
    height: 32px;
`;

const SidebarCategory = ({
    name,
    icon,
    classes,
    isOpen,
    isCollapsable,
    badge,
    ...rest
}: any) => {
    return (
        <Category {...rest}>
            {icon}
            <CategoryText>{name}</CategoryText>
            {isCollapsable ? (
                isOpen ? (
                    <CategoryIconMore />
                ) : (
                    <CategoryIconLess />
                )
            ) : null}
            {badge ? <CategoryBadge label={badge} /> : ""}
        </Category>
    );
};

const SidebarLink = ({
    name,
    to,
    badge,
}: {
    name: string;
    to: string;
    badge: React.ReactNode;
}) => {
    return (
        <NavLink to={to} exact activeClassName="active">
            <Link button dense>
                <LinkText>{name}</LinkText>
                {badge ? <LinkBadge label={badge} /> : ""}
            </Link>
        </NavLink>
    );
};

const Sidebar = ({
    classes,
    staticContext,
    location,
    history,
    match,
    routes,
    ...rest
}: any) => {
    const initOpenRoutes = () => {
        /* Open collapse element that matches current url */
        const pathName = location.pathname;

        let _routes = {};

        if (routes != null) {
            routes.forEach((route: any, index: number) => {
                const isActive = pathName.indexOf(route.path) === 0;
                const isOpen = route.open;
                const isHome = route.containsHome && pathName === "/";

                _routes = Object.assign({}, _routes, {
                    [index]: isActive || isOpen || isHome,
                });
            });
        }

        return _routes;
    };

    const [openRoutes, setOpenRoutes] = useState<{ [key: string]: boolean }>(
        () => initOpenRoutes()
    );

    const toggle = (index: string) => {
        // Collapse all elements
        Object.keys(openRoutes).forEach(
            (item) =>
                openRoutes[index] ||
                setOpenRoutes((openRoutes) =>
                    Object.assign({}, openRoutes, { [item]: false })
                )
        );

        // Toggle selected element
        setOpenRoutes((openRoutes) =>
            Object.assign({}, openRoutes, { [index]: !openRoutes[index] })
        );
    };

    const appContext = React.useContext(AppContext);

    return (
        <Drawer variant="permanent" {...rest}>
            <Brand button>
                <BrandIcon />
                <Box ml={1}>Reports</Box>
            </Brand>
            <Scrollbar>
                <List disablePadding>
                    <Items>
                        {routes.map((category: any, index: number) => {
                            return (
                                <React.Fragment key={index}>
                                    {category.header ? (
                                        <SidebarSection>
                                            {category.header}
                                        </SidebarSection>
                                    ) : null}

                                    {category.children && category.icon ? (
                                        <React.Fragment key={index}>
                                            <SidebarCategory
                                                isOpen={!openRoutes[index]}
                                                isCollapsable={true}
                                                name={category.id}
                                                icon={category.icon}
                                                button={true}
                                                onClick={() =>
                                                    toggle(String(index))
                                                }
                                            />
                                            <Collapse
                                                in={openRoutes[index]}
                                                timeout="auto"
                                                unmountOnExit
                                            >
                                                {category.children.map(
                                                    (
                                                        route: any,
                                                        index: string
                                                    ) => (
                                                        <SidebarLink
                                                            key={index}
                                                            name={route.name}
                                                            to={route.path}
                                                            badge={route.badge}
                                                        />
                                                    )
                                                )}
                                            </Collapse>
                                        </React.Fragment>
                                    ) : category.icon ? (
                                        <SidebarCategory
                                            isCollapsable={false}
                                            name={category.id}
                                            to={category.path}
                                            activeClassName="active"
                                            component={NavLink}
                                            icon={category.icon}
                                            exact
                                            button
                                            badge={category.badge}
                                        />
                                    ) : null}
                                </React.Fragment>
                            );
                        })}
                    </Items>
                </List>
            </Scrollbar>
            <SidebarFooter>
                <Grid container spacing={2}>
                    <Grid item>
                        <SidebarFooterBadge
                            overlap="circle"
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                            }}
                            variant="dot"
                        >
                            <Avatar
                                alt={appContext.user?.name}
                                src="/static/img/avatars/avatar-1.jpg"
                            />
                        </SidebarFooterBadge>
                    </Grid>
                    <Grid item>
                        <SidebarFooterText variant="body2">
                            {appContext.user?.name}
                        </SidebarFooterText>
                        <SidebarFooterSubText variant="caption">
                            <Role role={appContext.user?.role} />
                        </SidebarFooterSubText>
                    </Grid>
                </Grid>
            </SidebarFooter>
        </Drawer>
    );
};

export default withRouter(Sidebar);
